import type { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { html, nothing } from 'lit';
import { api } from '../api.js';
import { FKProjectChanged } from '../contexts.js';
import { forgeUtils } from '../forgeUtils.js';
import { PageProjectSettings } from '../page-project-settings.js';


export class ProjectAlerts {
  static maybeRender(project: WireTypes.Project | undefined, snapshotId: WireTypes.DataSnapshotId | undefined, serverInfo?: WireTypes.ServerInfo) {
    if (!project)
      return nothing;
    const messages = [];
    if (!project.org.allowedVisibilities.includes(project.visibility)) {
      messages.push(html`
        <fk-callout variant="warn">
          <strong>Project is read-only</strong><br />
          This project's visibility (<strong>${project.visibility}</strong>) is not supported by your current plan.
          Uploads are blocked until you change the project visibility in
          <a style="text-decoration: underline;" href="${PageProjectSettings.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId })}">settings</a> or upgrade your plan.
        </fk-callout>
      `);
    }
    // Only 403 is actionable by the user: it means GitHub refused the write, which
    // happens while an app permission is still waiting on an owner's approval. Other
    // codes are transient and would only add noise here. The install page sends the
    // viewer to their existing installation, which is where GitHub surfaces a pending
    // permission request.
    const approveUrl = serverInfo?.githubAppPublicURL ? serverInfo.githubAppPublicURL.replace(/\/$/, '') + '/installations/new' : undefined;
    // Both GitHub callouts below ask for something only an editor can do: approve the
    // app permissions, or turn the feature off in project settings. Everyone else --
    // org members, and logged-out visitors of a public project -- can do neither, so
    // the callout would be an instruction they cannot follow.
    const canEditProject = project.access === 'editor';
    if (canEditProject && forgeUtils.isGithub(project) && project.githubChecksHTTPCode === 403) {
      messages.push(html`
        <fk-callout variant="warn">
          <strong>GitHub reporting failed</strong><br />
          GitHub denied access when Flakiness.io last tried to post status checks.
          This usually means the Flakiness.io app is waiting for an organization owner to approve its updated permissions on GitHub.
          <br /><br />
          Either ${approveUrl ? html`<a style="text-decoration: underline;" target="_blank" href=${approveUrl}>approve the updated GitHub App permissions</a>` : html`approve the updated GitHub App permissions on GitHub`},
          or turn reporting off in
          <a style="text-decoration: underline;" href="${PageProjectSettings.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId })}">project settings</a>.
        </fk-callout>
      `);
    }
    // Pull request comments are new, and an installation created before them has no
    // permission to write one. That is not a broken project, so it reads as an
    // announcement rather than a failure.
    if (canEditProject && forgeUtils.isGithub(project) && project.githubPullRequestCommentsHTTPCode === 403) {
      messages.push(html`
        <fk-callout variant="success">
          <strong>Introducing Pull Request Comments</strong><br />
          <p>
          Flakiness.io can now post a test summary comment on every pull request.
          GitHub needs one new permission first: <strong>Pull requests: Read and write</strong>.
          Once an organization owner approves it, comments start appearing on the next test run.
          </p>
          <p>
          Pull request comments are enabled by default. You can disable this feature in
          <a style="text-decoration: underline;" href="${PageProjectSettings.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId })}">project settings</a>.
          </p>
          <h-box style="gap: var(--sl-spacing-x-small); margin-top: var(--sl-spacing-medium);">
            ${approveUrl ? html`
              <sl-button size="small" variant="success" target="_blank" href=${approveUrl}>Approve on GitHub</sl-button>
            ` : nothing}
            <sl-button size="small" @click=${(event: Event) => ProjectAlerts._dismissPullRequestComments(event, project)}>Done</sl-button>
          </h-box>
        </fk-callout>
      `);
    }
    if (project.sourceLastFetchHTTPCode === undefined) {
      messages.push(html`
        <fk-callout variant="tip">
          <strong>Commit History is loading</strong><br />
          It might take a few minutes for the commit history to load.
        </fk-callout>
      `);
    } else if (forgeUtils.isAccessDenied(project)) {
      messages.push(html`
        <fk-callout variant="warn">
          <strong>Git Repository Access Denied</strong><br />
          Open
          <a style="text-decoration: underline;" href="${PageProjectSettings.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId })}">project settings</a>
          to reconnect the repository or refresh ${forgeUtils.providerName(project)} access.
        </fk-callout>
      `);
    } else if (forgeUtils.isUnavailable(project)) {
      messages.push(html`
        <fk-callout variant="warn">
          <strong>${forgeUtils.providerName(project)} API is temporarily unavailable</strong><br />
          ${forgeUtils.providerName(project)} returned an error while we were updating this project.
          Commit history and ${forgeUtils.pullRequestLabel(project).toLowerCase()} data might be stale, but updates will resume once ${forgeUtils.providerName(project)} recovers.
        </fk-callout>
      `);
    } else if (project.sourceLastFetchHTTPCode !== 200) {
      messages.push(html`
        <fk-callout variant="danger">
          <strong>Failed to access Git Repository </strong><br />
          Something went wrong; please reach out to us at <fk-contact-us></fk-contact-us>
        </fk-callout>
      `);
    }
    return messages.length ? html`<v-box>${messages}</v-box>` : nothing;
  }

  // Clearing the code only retires the alert: the worker records a fresh 403 on the
  // next reporting run if the permission was never actually granted.
  private static async _dismissPullRequestComments(event: Event, project: WireTypes.Project) {
    await api.project.dismissGithubPullRequestCommentsAlert.POST({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
    });
    FKProjectChanged.dispatch(event.target as Element, project);
  }
}
